import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AddCustomizationChoicesApplicationErrors,
  AddCustomizationChoicesOptions,
  AddCustomizationChoicesResponse,
  AddCustomizationChoicesValidationErrors,
  BulkAddCustomizationChoicesApplicationErrors,
  BulkAddCustomizationChoicesOptions,
  BulkAddCustomizationChoicesResponse,
  BulkAddCustomizationChoicesValidationErrors,
  BulkCreateCustomizationsApplicationErrors,
  BulkCreateCustomizationsOptions,
  BulkCreateCustomizationsResponse,
  BulkCreateCustomizationsValidationErrors,
  BulkUpdateCustomizationsOptions,
  BulkUpdateCustomizationsResponse,
  Choice,
  CreateCustomizationApplicationErrors,
  CreateCustomizationValidationErrors,
  Customization,
  CustomizationChoices,
  CustomizationCreatedEnvelope,
  CustomizationDeletedEnvelope,
  CustomizationQuery,
  CustomizationUpdatedEnvelope,
  CustomizationsQueryBuilder,
  DeleteCustomizationValidationErrors,
  GetCustomizationOptions,
  MaskedCustomization,
  QueryCustomizationsOptions,
  QueryCustomizationsResponse,
  RemoveCustomizationChoicesApplicationErrors,
  RemoveCustomizationChoicesOptions,
  RemoveCustomizationChoicesResponse,
  RemoveCustomizationChoicesValidationErrors,
  ReorderCustomizationChoicesApplicationErrors,
  ReorderCustomizationChoicesOptions,
  ReorderCustomizationChoicesResponse,
  SetCustomizationChoicesApplicationErrors,
  SetCustomizationChoicesOptions,
  SetCustomizationChoicesResponse,
  SetCustomizationChoicesValidationErrors,
  UpdateCustomization,
  UpdateCustomizationChoicesApplicationErrors,
  UpdateCustomizationChoicesOptions,
  UpdateCustomizationChoicesResponse,
  UpdateCustomizationChoicesValidationErrors,
  UpdateCustomizationOptions,
  addCustomizationChoices as universalAddCustomizationChoices,
  bulkAddCustomizationChoices as universalBulkAddCustomizationChoices,
  bulkCreateCustomizations as universalBulkCreateCustomizations,
  bulkUpdateCustomizations as universalBulkUpdateCustomizations,
  createCustomization as universalCreateCustomization,
  deleteCustomization as universalDeleteCustomization,
  getCustomization as universalGetCustomization,
  queryCustomizations as universalQueryCustomizations,
  removeCustomizationChoices as universalRemoveCustomizationChoices,
  reorderCustomizationChoices as universalReorderCustomizationChoices,
  setCustomizationChoices as universalSetCustomizationChoices,
  typedQueryCustomizations as universalTypedQueryCustomizations,
  updateCustomization as universalUpdateCustomization,
  updateCustomizationChoices as universalUpdateCustomizationChoices,
} from './stores-catalog-v3-customization-customizations-v-3.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/stores' };

export function createCustomization(
  httpClient: HttpClient
): CreateCustomizationSignature {
  return (
    customization: NonNullablePaths<
      Customization,
      | `choicesSettings.choices`
      | `customizationRenderType`
      | `customizationType`
      | `freeTextInput.title`
      | `name`,
      3
    >
  ) =>
    universalCreateCustomization(
      customization,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateCustomizationSignature {
  /**
   * Creates a customization.
   * @param - Customization to create.
   * @returns Created customization.
   */
  (
    customization: NonNullablePaths<
      Customization,
      | `choicesSettings.choices`
      | `customizationRenderType`
      | `customizationType`
      | `freeTextInput.title`
      | `name`,
      3
    >
  ): Promise<
    NonNullablePaths<
      Customization,
      | `freeTextInput.minCharCount`
      | `freeTextInput.maxCharCount`
      | `freeTextInput.title`
      | `choicesSettings.choices`
      | `choicesSettings.choices.${number}.colorCode`
      | `choicesSettings.choices.${number}._id`
      | `choicesSettings.choices.${number}.choiceType`
      | `choicesSettings.choices.${number}.name`
      | `name`
      | `customizationType`
      | `customizationRenderType`,
      5
    > & {
      __applicationErrorsType?: CreateCustomizationApplicationErrors;
      __validationErrorsType?: CreateCustomizationValidationErrors;
    }
  >;
}

export function getCustomization(
  httpClient: HttpClient
): GetCustomizationSignature {
  return (customizationId: string, options?: GetCustomizationOptions) =>
    universalGetCustomization(
      customizationId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetCustomizationSignature {
  /**
   * Retrieves a customization.
   * @param - Customization ID.
   * @returns Customization.
   */
  (customizationId: string, options?: GetCustomizationOptions): Promise<
    NonNullablePaths<
      Customization,
      | `freeTextInput.minCharCount`
      | `freeTextInput.maxCharCount`
      | `freeTextInput.title`
      | `choicesSettings.choices`
      | `choicesSettings.choices.${number}.colorCode`
      | `choicesSettings.choices.${number}._id`
      | `choicesSettings.choices.${number}.choiceType`
      | `choicesSettings.choices.${number}.name`
      | `name`
      | `customizationType`
      | `customizationRenderType`,
      5
    >
  >;
}

export function updateCustomization(
  httpClient: HttpClient
): UpdateCustomizationSignature {
  return (
    _id: string,
    customization: NonNullablePaths<UpdateCustomization, `revision`, 2>,
    options?: UpdateCustomizationOptions
  ) =>
    universalUpdateCustomization(
      _id,
      customization,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateCustomizationSignature {
  /**
   * Updates a customization.
   *
   * Each time the customization is updated, `revision` increments by 1.
   * The current `revision` must be passed when updating the customization.
   * This ensures you're working with the latest customization and prevents unintended overwrites.
   * @param - Customization ID.
   * @returns Updated customization.
   */
  (
    _id: string,
    customization: NonNullablePaths<UpdateCustomization, `revision`, 2>,
    options?: UpdateCustomizationOptions
  ): Promise<
    NonNullablePaths<
      Customization,
      | `freeTextInput.minCharCount`
      | `freeTextInput.maxCharCount`
      | `freeTextInput.title`
      | `choicesSettings.choices`
      | `choicesSettings.choices.${number}.colorCode`
      | `choicesSettings.choices.${number}._id`
      | `choicesSettings.choices.${number}.choiceType`
      | `choicesSettings.choices.${number}.name`
      | `name`
      | `customizationType`
      | `customizationRenderType`,
      5
    >
  >;
}

export function deleteCustomization(
  httpClient: HttpClient
): DeleteCustomizationSignature {
  return (customizationId: string) =>
    universalDeleteCustomization(
      customizationId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteCustomizationSignature {
  /**
   * Deletes a customization.
   *
   * > **Notes:**
   * > + **Options** (customizations with `customizationType: PRODUCT_OPTION`) can't be deleted while assigned to any product because they affect product variants and inventory.
   * > + **Modifiers** can be deleted freely. When deleted, they are automatically removed from all products that reference them.
   * @param - Customization ID.
   */
  (customizationId: string): Promise<
    void & {
      __validationErrorsType?: DeleteCustomizationValidationErrors;
    }
  >;
}

export function queryCustomizations(
  httpClient: HttpClient
): QueryCustomizationsSignature {
  return (options?: QueryCustomizationsOptions) =>
    universalQueryCustomizations(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryCustomizationsSignature {
  /**
   * Retrieves a list of up to 100 customizations, given the provided filtering, sorting, and cursor paging.
   * Pass supported values to the `fields` array in the request to include those fields in the response.
   *
   *
   * Query Customizations runs with these defaults, which you can override:
   *
   * - `createdDate` is sorted in `DESC` order
   * - `cursorPaging.limit` is `100`
   *
   * To learn about working with _Query_ endpoints, see
   * [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language),
   * and [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging).
   */
  (options?: QueryCustomizationsOptions): CustomizationsQueryBuilder;
}

export function typedQueryCustomizations(
  httpClient: HttpClient
): TypedQueryCustomizationsSignature {
  return (query: CustomizationQuery, options?: QueryCustomizationsOptions) =>
    universalTypedQueryCustomizations(
      query,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryCustomizationsSignature {
  /** */
  (query: CustomizationQuery, options?: QueryCustomizationsOptions): Promise<
    NonNullablePaths<
      QueryCustomizationsResponse,
      | `customizations`
      | `customizations.${number}.freeTextInput.minCharCount`
      | `customizations.${number}.freeTextInput.maxCharCount`
      | `customizations.${number}.freeTextInput.title`
      | `customizations.${number}.name`
      | `customizations.${number}.customizationType`
      | `customizations.${number}.customizationRenderType`,
      5
    >
  >;
}

export function bulkCreateCustomizations(
  httpClient: HttpClient
): BulkCreateCustomizationsSignature {
  return (
    customizations: Customization[],
    options?: BulkCreateCustomizationsOptions
  ) =>
    universalBulkCreateCustomizations(
      customizations,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreateCustomizationsSignature {
  /**
   * Creates multiple customizations.
   * @param - Customizations to create.
   */
  (
    customizations: Customization[],
    options?: BulkCreateCustomizationsOptions
  ): Promise<
    NonNullablePaths<
      BulkCreateCustomizationsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.customization.freeTextInput.minCharCount`
      | `results.${number}.customization.freeTextInput.maxCharCount`
      | `results.${number}.customization.freeTextInput.title`
      | `results.${number}.customization.name`
      | `results.${number}.customization.customizationType`
      | `results.${number}.customization.customizationRenderType`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __applicationErrorsType?: BulkCreateCustomizationsApplicationErrors;
      __validationErrorsType?: BulkCreateCustomizationsValidationErrors;
    }
  >;
}

export function addCustomizationChoices(
  httpClient: HttpClient
): AddCustomizationChoicesSignature {
  return (
    customizationId: string,
    choices: Choice[],
    options?: AddCustomizationChoicesOptions
  ) =>
    universalAddCustomizationChoices(
      customizationId,
      choices,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface AddCustomizationChoicesSignature {
  /**
   * Adds choices to a customization.
   * @param - Customization ID.
   * @param - Choices to add.
   */
  (
    customizationId: string,
    choices: Choice[],
    options?: AddCustomizationChoicesOptions
  ): Promise<
    NonNullablePaths<
      AddCustomizationChoicesResponse,
      | `customization.freeTextInput.minCharCount`
      | `customization.freeTextInput.maxCharCount`
      | `customization.freeTextInput.title`
      | `customization.choicesSettings.choices`
      | `customization.choicesSettings.choices.${number}.colorCode`
      | `customization.choicesSettings.choices.${number}._id`
      | `customization.choicesSettings.choices.${number}.choiceType`
      | `customization.choicesSettings.choices.${number}.name`
      | `customization.name`
      | `customization.customizationType`
      | `customization.customizationRenderType`,
      6
    > & {
      __applicationErrorsType?: AddCustomizationChoicesApplicationErrors;
      __validationErrorsType?: AddCustomizationChoicesValidationErrors;
    }
  >;
}

export function setCustomizationChoices(
  httpClient: HttpClient
): SetCustomizationChoicesSignature {
  return (
    customizationId: string,
    choices: Choice[],
    options?: SetCustomizationChoicesOptions
  ) =>
    universalSetCustomizationChoices(
      customizationId,
      choices,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface SetCustomizationChoicesSignature {
  /**
   * Sets a customization's choices. Any and all existing choices will be overridden.
   *
   * > **Note:** A choice cannot be overridden if it is assigned to one or more products.
   * @param - Customization ID.
   * @param - Choices to set.
   */
  (
    customizationId: string,
    choices: Choice[],
    options?: SetCustomizationChoicesOptions
  ): Promise<
    NonNullablePaths<
      SetCustomizationChoicesResponse,
      | `customization.freeTextInput.minCharCount`
      | `customization.freeTextInput.maxCharCount`
      | `customization.freeTextInput.title`
      | `customization.choicesSettings.choices`
      | `customization.choicesSettings.choices.${number}.colorCode`
      | `customization.choicesSettings.choices.${number}._id`
      | `customization.choicesSettings.choices.${number}.choiceType`
      | `customization.choicesSettings.choices.${number}.name`
      | `customization.name`
      | `customization.customizationType`
      | `customization.customizationRenderType`,
      6
    > & {
      __applicationErrorsType?: SetCustomizationChoicesApplicationErrors;
      __validationErrorsType?: SetCustomizationChoicesValidationErrors;
    }
  >;
}

export function removeCustomizationChoices(
  httpClient: HttpClient
): RemoveCustomizationChoicesSignature {
  return (
    customizationId: string,
    choiceIds: string[],
    options?: RemoveCustomizationChoicesOptions
  ) =>
    universalRemoveCustomizationChoices(
      customizationId,
      choiceIds,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface RemoveCustomizationChoicesSignature {
  /**
   * Removes choices from a customization.
   *
   * > **Note:** A choice can't be removed if it's assigned to one or more product variants. This applies to option choices only; modifier choices can be removed freely.
   * @param - Customization ID.
   * @param - IDs of choices to remove.
   */
  (
    customizationId: string,
    choiceIds: string[],
    options?: RemoveCustomizationChoicesOptions
  ): Promise<
    NonNullablePaths<
      RemoveCustomizationChoicesResponse,
      | `customization.freeTextInput.minCharCount`
      | `customization.freeTextInput.maxCharCount`
      | `customization.freeTextInput.title`
      | `customization.choicesSettings.choices`
      | `customization.choicesSettings.choices.${number}.colorCode`
      | `customization.choicesSettings.choices.${number}._id`
      | `customization.choicesSettings.choices.${number}.choiceType`
      | `customization.choicesSettings.choices.${number}.name`
      | `customization.name`
      | `customization.customizationType`
      | `customization.customizationRenderType`,
      6
    > & {
      __applicationErrorsType?: RemoveCustomizationChoicesApplicationErrors;
      __validationErrorsType?: RemoveCustomizationChoicesValidationErrors;
    }
  >;
}

/** @internal */
export function updateCustomizationChoices(
  httpClient: HttpClient
): UpdateCustomizationChoicesSignature {
  return (
    customizationId: string,
    revision: string,
    options?: NonNullablePaths<
      UpdateCustomizationChoicesOptions,
      `choices` | `choices.${number}.choice`,
      4
    >
  ) =>
    universalUpdateCustomizationChoices(
      customizationId,
      revision,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateCustomizationChoicesSignature {
  /**
   * Updates properties of existing choices in a customization.
   * Choices are identified by their IDs. Only the fields specified in `fieldMask` are updated.
   *
   * Currently supports updating `primaryChoiceIds`.
   * @param - Customization ID.
   * @param - Revision number, which increments by 1 each time the customization is updated.
   * To prevent conflicting changes,
   * the current revision must be passed when updating the customization.
   */
  (
    customizationId: string,
    revision: string,
    options?: NonNullablePaths<
      UpdateCustomizationChoicesOptions,
      `choices` | `choices.${number}.choice`,
      4
    >
  ): Promise<
    NonNullablePaths<
      UpdateCustomizationChoicesResponse,
      | `customization.freeTextInput.minCharCount`
      | `customization.freeTextInput.maxCharCount`
      | `customization.freeTextInput.title`
      | `customization.choicesSettings.choices`
      | `customization.choicesSettings.choices.${number}.colorCode`
      | `customization.choicesSettings.choices.${number}._id`
      | `customization.choicesSettings.choices.${number}.choiceType`
      | `customization.choicesSettings.choices.${number}.name`
      | `customization.name`
      | `customization.customizationType`
      | `customization.customizationRenderType`,
      6
    > & {
      __applicationErrorsType?: UpdateCustomizationChoicesApplicationErrors;
      __validationErrorsType?: UpdateCustomizationChoicesValidationErrors;
    }
  >;
}

/** @internal */
export function reorderCustomizationChoices(
  httpClient: HttpClient
): ReorderCustomizationChoicesSignature {
  return (
    customizationId: string,
    revision: string,
    options?: NonNullablePaths<
      ReorderCustomizationChoicesOptions,
      `choiceIds`,
      2
    >
  ) =>
    universalReorderCustomizationChoices(
      customizationId,
      revision,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ReorderCustomizationChoicesSignature {
  /**
   * Reorders a customization's choices. The new order is persisted and reflected in the customization's choices list.
   * Calling this endpoint sets `choicesSettings.sortOrder` to `MANUAL`.
   *
   * To control how choices are sorted in storefront filter panels, use [Update Customization](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/customizations-v3/update-customization) to set the `choicesSettings.sortOrder` field.
   * @param - Customization ID.
   * @param - Revision number, which increments by 1 each time the customization is updated. To prevent conflicting changes, the current revision must be passed when updating the customization.
   */
  (
    customizationId: string,
    revision: string,
    options?: NonNullablePaths<
      ReorderCustomizationChoicesOptions,
      `choiceIds`,
      2
    >
  ): Promise<
    NonNullablePaths<
      ReorderCustomizationChoicesResponse,
      | `customization.freeTextInput.minCharCount`
      | `customization.freeTextInput.maxCharCount`
      | `customization.freeTextInput.title`
      | `customization.choicesSettings.choices`
      | `customization.choicesSettings.choices.${number}.colorCode`
      | `customization.choicesSettings.choices.${number}._id`
      | `customization.choicesSettings.choices.${number}.choiceType`
      | `customization.choicesSettings.choices.${number}.name`
      | `customization.name`
      | `customization.customizationType`
      | `customization.customizationRenderType`,
      6
    > & {
      __applicationErrorsType?: ReorderCustomizationChoicesApplicationErrors;
    }
  >;
}

export function bulkAddCustomizationChoices(
  httpClient: HttpClient
): BulkAddCustomizationChoicesSignature {
  return (
    customizationsChoices: NonNullablePaths<
      CustomizationChoices,
      `choices` | `customizationId`,
      2
    >[],
    options?: BulkAddCustomizationChoicesOptions
  ) =>
    universalBulkAddCustomizationChoices(
      customizationsChoices,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkAddCustomizationChoicesSignature {
  /**
   * Adds choices to multiple customizations.
   * @param - List of customization IDs and choices.
   */
  (
    customizationsChoices: NonNullablePaths<
      CustomizationChoices,
      `choices` | `customizationId`,
      2
    >[],
    options?: BulkAddCustomizationChoicesOptions
  ): Promise<
    NonNullablePaths<
      BulkAddCustomizationChoicesResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.customization.freeTextInput.minCharCount`
      | `results.${number}.customization.freeTextInput.maxCharCount`
      | `results.${number}.customization.freeTextInput.title`
      | `results.${number}.customization.name`
      | `results.${number}.customization.customizationType`
      | `results.${number}.customization.customizationRenderType`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __applicationErrorsType?: BulkAddCustomizationChoicesApplicationErrors;
      __validationErrorsType?: BulkAddCustomizationChoicesValidationErrors;
    }
  >;
}

export function bulkUpdateCustomizations(
  httpClient: HttpClient
): BulkUpdateCustomizationsSignature {
  return (
    customizations: NonNullablePaths<
      MaskedCustomization,
      `customization._id` | `customization.name` | `customization.revision`,
      3
    >[],
    options?: BulkUpdateCustomizationsOptions
  ) =>
    universalBulkUpdateCustomizations(
      customizations,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateCustomizationsSignature {
  /**
   * Updates multiple customizations.
   *
   * Each time the customization is updated, `revision` increments by 1.
   * The current `revision` must be passed when updating the customization.
   * This ensures you're working with the latest customization and prevents unintended overwrites.
   * @param - List of customizations to update.
   */
  (
    customizations: NonNullablePaths<
      MaskedCustomization,
      `customization._id` | `customization.name` | `customization.revision`,
      3
    >[],
    options?: BulkUpdateCustomizationsOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateCustomizationsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.customization.freeTextInput.minCharCount`
      | `results.${number}.customization.freeTextInput.maxCharCount`
      | `results.${number}.customization.freeTextInput.title`
      | `results.${number}.customization.name`
      | `results.${number}.customization.customizationType`
      | `results.${number}.customization.customizationRenderType`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export const onCustomizationCreated = EventDefinition(
  'wix.stores.catalog.v3.customization_created',
  true,
  (event: CustomizationCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'entity.choicesSettings.choices.image' },
            { path: 'entity.choicesSettings.choices.displayImage' },
          ],
        },
      ])
    )
)<CustomizationCreatedEnvelope>();
export const onCustomizationDeleted = EventDefinition(
  'wix.stores.catalog.v3.customization_deleted',
  true,
  (event: CustomizationDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'entity.choicesSettings.choices.image' },
            { path: 'entity.choicesSettings.choices.displayImage' },
          ],
        },
      ])
    )
)<CustomizationDeletedEnvelope>();
export const onCustomizationUpdated = EventDefinition(
  'wix.stores.catalog.v3.customization_updated',
  true,
  (event: CustomizationUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'entity.choicesSettings.choices.image' },
            { path: 'entity.choicesSettings.choices.displayImage' },
            { path: 'modifiedFields.choicesSettings.choices.image' },
            { path: 'modifiedFields.choicesSettings.choices.displayImage' },
          ],
        },
      ])
    )
)<CustomizationUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  AddCustomizationChoicesOptions,
  AddCustomizationChoicesRequest,
  AddCustomizationChoicesResponse,
  App,
  ApplicationError,
  BaseEventMetadata,
  BulkActionMetadata,
  BulkAddCustomizationChoicesOptions,
  BulkAddCustomizationChoicesRequest,
  BulkAddCustomizationChoicesResponse,
  BulkCreateCustomizationsForMigrationRequest,
  BulkCreateCustomizationsForMigrationResponse,
  BulkCreateCustomizationsOptions,
  BulkCreateCustomizationsRequest,
  BulkCreateCustomizationsResponse,
  BulkCustomizationResult,
  BulkUpdateCustomizationsOptions,
  BulkUpdateCustomizationsRequest,
  BulkUpdateCustomizationsResponse,
  Choice,
  ChoiceIdsMismatchData,
  ChoiceSortStrategy,
  ChoiceType,
  ChoiceValueOneOf,
  ChoicesSettings,
  CreateCustomizationRequest,
  CreateCustomizationResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  CustomTag,
  Customization,
  CustomizationChoices,
  CustomizationCreatedEnvelope,
  CustomizationCustomizationSettingsOneOf,
  CustomizationDeletedEnvelope,
  CustomizationIdsWrapper,
  CustomizationQuerySpec,
  CustomizationRenderType,
  CustomizationType,
  CustomizationUpdatedEnvelope,
  CustomizationsQueryBuilder,
  CustomizationsQueryResult,
  DeleteCustomizationRequest,
  DeleteCustomizationResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  File,
  FocalPoint,
  FreeTextSettings,
  GetCustomizationOptions,
  GetCustomizationRequest,
  GetCustomizationResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  InvalidateCache,
  InvalidateCacheGetByOneOf,
  ItemMetadata,
  MaskedChoice,
  MaskedCustomization,
  MessageEnvelope,
  MultipleColors,
  Page,
  Pages,
  QueryCustomizationsOptions,
  QueryCustomizationsRequest,
  QueryCustomizationsResponse,
  RecloneSiteRequest,
  RecloneSiteResponse,
  RemoveCustomizationChoicesOptions,
  RemoveCustomizationChoicesRequest,
  RemoveCustomizationChoicesResponse,
  ReorderCustomizationChoicesOptions,
  ReorderCustomizationChoicesRequest,
  ReorderCustomizationChoicesResponse,
  RequestedFields,
  RestoreInfo,
  SetCustomizationChoicesOptions,
  SetCustomizationChoicesRequest,
  SetCustomizationChoicesResponse,
  SortOrder,
  Sorting,
  URI,
  URIs,
  UpdateCustomization,
  UpdateCustomizationChoicesOptions,
  UpdateCustomizationChoicesRequest,
  UpdateCustomizationChoicesResponse,
  UpdateCustomizationOptions,
  UpdateCustomizationRequest,
  UpdateCustomizationResponse,
  WebhookIdentityType,
  utils,
} from './stores-catalog-v3-customization-customizations-v-3.universal.js';
